// @flow strict
import { loadableReady } from '@loadable/component';
import routeLoadingLabels from '../routeLoading/labels.js';
import reloadPageOnClick from '../routeLoading/reloadPage.js';

// loadableReady intentionally waits forever when a required <script> fails. Keep
// the server-rendered page available and offer a native retry outside its root.
export default function hydrateWhenReady({
  container,
  hydrate,
  language,
  ready = loadableReady,
  timeoutMs = 20000,
}: {
  container: HTMLElement,
  hydrate: () => void,
  language: string,
  ready?: () => Promise<any>,
  timeoutMs?: number,
}) {
  const doc = container.ownerDocument;
  const requiredScripts = new Set(doc.querySelectorAll('script[data-chunk][src]'));
  let hydrated = false;
  let cancelled = false;
  let notice = null;
  let timer;

  const showRecovery = () => {
    if (hydrated || cancelled || notice) return;
    const labels = routeLoadingLabels(language);
    notice = doc.createElement('aside');
    notice.setAttribute('role', 'alert');
    notice.setAttribute('data-hydration-recovery', '');
    notice.style.cssText =
      'position:fixed;right:16px;bottom:16px;left:16px;z-index:20000;' +
      'max-width:440px;margin-left:auto;padding:16px;box-sizing:border-box;' +
      'background:white;color:#222;border:2px solid #c51162;border-radius:8px;' +
      'box-shadow:0 2px 8px rgba(0,0,0,.2);font:16px/1.5 sans-serif;';
    const message = doc.createElement('p');
    message.style.margin = '0 0 8px';
    message.textContent = labels.unavailable;
    const reload = doc.createElement('a');
    reload.href = doc.defaultView ? doc.defaultView.location.href : '/';
    reload.textContent = labels.reload;
    reload.addEventListener('click', (event) => reloadPageOnClick(event, doc.defaultView));
    reload.style.cssText = 'color:#9c0d4b;text-decoration:underline;font-weight:bold;';
    notice.append(message, reload);
    container.insertAdjacentElement('afterend', notice);
  };

  const onResourceError = (event) => {
    if (requiredScripts.has(event.target)) showRecovery();
  };

  const cleanup = () => {
    clearTimeout(timer);
    doc.removeEventListener('error', onResourceError, true);
    if (notice) {
      notice.remove();
      notice = null;
    }
  };

  const hydrateOnce = () => {
    if (hydrated || cancelled) return;
    hydrated = true;
    cleanup();
    hydrate();
  };

  // Capture script errors that occur after the entry bundle starts; the timeout
  // also covers an error that happened before the listener could be installed.
  doc.addEventListener('error', onResourceError, true);
  timer = setTimeout(showRecovery, timeoutMs);
  try {
    Promise.resolve(ready()).then(hydrateOnce, showRecovery);
  } catch (error) {
    showRecovery();
  }

  return () => {
    cancelled = true;
    cleanup();
  };
}
